import { createApiClient, TokenStore } from '@forgedas/shared-core';
import {
  clearActiveCompanyContext,
  getActiveCompanyId,
  shouldApplyActiveCompany,
} from '../utils/activeCompany';
import { appLogger } from './logger';
import { tokenStorage } from './tokenStorage';

// Prefer runtime-injected URL (set by start.js at deploy time, or by the
// Electron desktop shell at preload time) over build-time CRA env var.
export const API_BASE_URL =
  (typeof window !== 'undefined' && (window as any).__RUNTIME_API_URL__) ||
  process.env.REACT_APP_API_URL ||
  '/api';

// Get the backend base URL (without /api suffix) for direct file access
export const getBackendBaseUrl = (): string => {
  // If API_BASE_URL is relative (/api), use window.location.origin
  if (API_BASE_URL.startsWith('/')) {
    return window.location.origin;
  }
  // If API_BASE_URL is absolute, strip /api suffix to get base URL
  return API_BASE_URL.replace(/\/api\/?$/, '');
};

function readStoredUser(): unknown {
  try {
    const raw = sessionStorage.getItem('user') || localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/**
 * TokenStore adapter for createApiClient — delegates to the cross-platform
 * tokenStorage facade. On desktop, writes/clears propagate to safeStorage;
 * on web, they preserve the existing session/localStorage behaviour.
 */
const tokenStore: TokenStore = {
  getToken: () => tokenStorage.get('token'),
  setToken: (token: string) => tokenStorage.setMirroring('token', token),
  clearToken: () => tokenStorage.remove('token'),
};

const { axios: api } = createApiClient({
  baseUrl: API_BASE_URL,
  tokenStore,
  getActiveCompanyId,
  shouldApplyActiveCompany: (user, requestUrl) =>
    shouldApplyActiveCompany(user as any, requestUrl),
  getStoredUser: readStoredUser,
  clearActiveCompanyContext,
  onSessionExpired: () => {
    window.dispatchEvent(new Event('auth:session-expired'));
  },
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const cfg = error?.config || {};
    await appLogger.error('api', 'request failed', {
      method: cfg.method,
      url: cfg.url,
      status,
      message: error?.message || 'unknown error',
    });
    return Promise.reject(error);
  },
);

export default api;
